import { Chip, Spinner } from '@nextui-org/react'
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '~/components/common/Button'
import { Input } from '~/components/common/Input'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import {
  AdjustCreditsResponse,
  BillingAccountData,
  LostCreditsInfo,
  SuperAdminCreditBalance,
  adjustSuperAdminCredits,
  findLostPurchasedCredits,
  getSuperAdminCreditBalance,
  overrideCreditValues,
  recoverLostPurchasedCredits
} from '~/services/superAdminService'
import { formatDate } from '~/utils/timezone'
import { BetterToast } from '~/utils/toast'

export const CreditsModal = ({
  isOpen,
  onClose,
  account
}: {
  isOpen: boolean
  onClose: () => void
  account: BillingAccountData | null
}) => {
  const [creditBalance, setCreditBalance] = useState<SuperAdminCreditBalance | null>(null)
  const [loading, setLoading] = useState(false)
  const [adjustAmount, setAdjustAmount] = useState('')
  const [adjustReason, setAdjustReason] = useState('')
  const [isAdjusting, setIsAdjusting] = useState(false)
  const [lostCredits, setLostCredits] = useState<LostCreditsInfo[] | null>(null)
  const [isCheckingLost, setIsCheckingLost] = useState(false)
  const [isRecovering, setIsRecovering] = useState(false)
  const [overridePurchased, setOverridePurchased] = useState('')
  const [overrideCarriedOver, setOverrideCarriedOver] = useState('')
  const [overrideCreditDebt, setOverrideCreditDebt] = useState('')
  const [overrideCarryoverDebt, setOverrideCarryoverDebt] = useState('')
  const [isOverriding, setIsOverriding] = useState(false)

  const fetchCredits = useCallback(async () => {
    if (!account) return
    try {
      setLoading(true)
      const data = await getSuperAdminCreditBalance(account.id)
      setCreditBalance(data)
    } catch (err) {
      BetterToast.error(err instanceof Error ? err.message : 'Failed to load credit balance')
    } finally {
      setLoading(false)
    }
  }, [account])

  useEffect(() => {
    if (isOpen && account) {
      fetchCredits()
      setAdjustAmount('')
      setAdjustReason('')
      setOverridePurchased('')
      setOverrideCarriedOver('')
      setOverrideCreditDebt('')
      setOverrideCarryoverDebt('')
    }
  }, [isOpen, account, fetchCredits])

  useEffect(() => {
    if (creditBalance) {
      setOverridePurchased(creditBalance.purchased_credits.toString())
      setOverrideCarriedOver(creditBalance.carried_over_credits.toString())
      setOverrideCreditDebt(creditBalance.credit_debt.toString())
      setOverrideCarryoverDebt(creditBalance.carryover_debt.toString())
    }
  }, [creditBalance])

  const handleAdjust = async () => {
    if (!account) return
    const amount = parseInt(adjustAmount, 10)
    if (isNaN(amount) || amount === 0) {
      BetterToast.error('Enter a non-zero amount')
      return
    }
    if (!adjustReason.trim()) {
      BetterToast.error('Reason is required for audit trail')
      return
    }

    try {
      setIsAdjusting(true)
      const result: AdjustCreditsResponse = await adjustSuperAdminCredits(
        account.id,
        amount,
        adjustReason.trim()
      )
      BetterToast.success(
        `Credits adjusted: ${result.previous_purchased_credits} -> ${result.new_purchased_credits}`
      )
      setAdjustAmount('')
      setAdjustReason('')
      await fetchCredits()
    } catch (err) {
      BetterToast.error(err instanceof Error ? err.message : 'Failed to adjust credits')
    } finally {
      setIsAdjusting(false)
    }
  }

  const handleCreditOverride = async () => {
    if (!account) return
    try {
      setIsOverriding(true)
      const overrides: Record<string, number> = {}

      const newPurchased = overridePurchased.trim() !== '' ? parseInt(overridePurchased, 10) : null
      if (newPurchased !== null && newPurchased !== creditBalance?.purchased_credits) {
        overrides.purchased_credits = newPurchased
      }

      const newCarriedOver =
        overrideCarriedOver.trim() !== '' ? parseInt(overrideCarriedOver, 10) : null
      if (newCarriedOver !== null && newCarriedOver !== creditBalance?.carried_over_credits) {
        overrides.carried_over_credits = newCarriedOver
      }

      const newDebt = overrideCreditDebt.trim() !== '' ? parseInt(overrideCreditDebt, 10) : null
      if (newDebt !== null && newDebt !== creditBalance?.credit_debt) {
        overrides.credit_debt = newDebt
      }

      const newCarryoverDebt =
        overrideCarryoverDebt.trim() !== '' ? parseInt(overrideCarryoverDebt, 10) : null
      if (newCarryoverDebt !== null && newCarryoverDebt !== creditBalance?.carryover_debt) {
        overrides.carryover_debt = newCarryoverDebt
      }

      if (Object.keys(overrides).length === 0) {
        BetterToast.info('No changes to save')
        return
      }

      await overrideCreditValues(account.id, overrides)
      BetterToast.success('Credit values updated')
      await fetchCredits()
    } catch (err) {
      BetterToast.error(err instanceof Error ? err.message : 'Failed to override credits')
    } finally {
      setIsOverriding(false)
    }
  }

  const handleCheckLostCredits = async () => {
    if (!account) return
    try {
      setIsCheckingLost(true)
      const data = await findLostPurchasedCredits(account.id)
      const expiredWithCredits = data.filter(l => !l.is_active && l.purchased_credits > 0)
      setLostCredits(expiredWithCredits)
      if (expiredWithCredits.length === 0) {
        BetterToast.info('No lost credits found on expired licenses')
      }
    } catch (err) {
      BetterToast.error(err instanceof Error ? err.message : 'Failed to check for lost credits')
    } finally {
      setIsCheckingLost(false)
    }
  }

  const handleRecoverCredits = async () => {
    if (!account) return
    try {
      setIsRecovering(true)
      const result = await recoverLostPurchasedCredits(account.id)
      if (result.recovered_credits > 0) {
        BetterToast.success(
          `Recovered ${result.recovered_credits} credits from ${result.recovered_from_licenses.length} expired license(s). New purchased balance: ${result.new_purchased_credits}`
        )
      } else {
        BetterToast.info('No credits to recover')
      }
      setLostCredits(null)
      await fetchCredits()
    } catch (err) {
      BetterToast.error(err instanceof Error ? err.message : 'Failed to recover credits')
    } finally {
      setIsRecovering(false)
    }
  }

  if (!account) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} hideCloseButton={false} className='max-w-2xl'>
      <ModalBody>
        <h3 className='text-lg font-semibold mb-4'>
          Purchased Credits — {account.name} (#{account.id})
        </h3>

        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <Spinner size='lg' />
          </div>
        ) : creditBalance ? (
          <div className='flex flex-col gap-5'>
            <div className='grid grid-cols-3 gap-3'>
              <div className='border rounded-lg p-3 text-center'>
                <div className='text-xs text-gray-500 mb-1'>Total Available</div>
                <div className='text-xl font-bold text-green-700'>
                  {creditBalance.total_credits.toLocaleString()}
                </div>
              </div>
              <div className='border rounded-lg p-3 text-center'>
                <div className='text-xs text-gray-500 mb-1'>Subscription Remaining</div>
                <div className='text-xl font-bold text-blue-700'>
                  {creditBalance.subscription_remaining.toLocaleString()}
                </div>
                <div className='text-xs text-gray-400'>
                  {creditBalance.subscription_used.toLocaleString()} /{' '}
                  {creditBalance.subscription_limit.toLocaleString()} used
                </div>
              </div>
              <div className='border rounded-lg p-3 text-center'>
                <div className='text-xs text-gray-500 mb-1'>Purchased Credits</div>
                <div className='text-xl font-bold text-purple-700'>
                  {creditBalance.purchased_credits.toLocaleString()}
                </div>
              </div>
            </div>
            <div className='grid grid-cols-3 gap-3'>
              <div className='border rounded-lg p-3 text-center'>
                <div className='text-xs text-gray-500 mb-1'>Carried Over Credits</div>
                <div className='text-lg font-bold text-indigo-700'>
                  {creditBalance.carried_over_credits.toLocaleString()}
                </div>
              </div>
              <div className='border rounded-lg p-3 text-center'>
                <div className='text-xs text-gray-500 mb-1'>Credit Debt</div>
                <div
                  className={`text-lg font-bold ${creditBalance.credit_debt > 0 ? 'text-red-600' : 'text-gray-500'}`}
                >
                  {creditBalance.credit_debt.toLocaleString()}
                </div>
              </div>
              <div className='border rounded-lg p-3 text-center'>
                <div className='text-xs text-gray-500 mb-1'>Carryover Debt</div>
                <div
                  className={`text-lg font-bold ${creditBalance.carryover_debt > 0 ? 'text-orange-600' : 'text-gray-500'}`}
                >
                  {creditBalance.carryover_debt.toLocaleString()}
                </div>
                {creditBalance.carryover_debt > 0 && (
                  <div className='text-xs text-orange-500 mt-0.5'>from Flex overage</div>
                )}
              </div>
            </div>

            {creditBalance.period_end && (
              <div className='text-sm text-gray-500'>
                Billing period ends:{' '}
                <span className='font-semibold text-gray-700'>
                  {formatDate(creditBalance.period_end)}
                </span>
              </div>
            )}

            <div className='border rounded-lg p-4 bg-gray-50'>
              <h4 className='text-sm font-semibold text-gray-700 mb-3'>Adjust Purchased Credits</h4>
              <div className='flex flex-col gap-3'>
                <div className='flex gap-3'>
                  <Input
                    type='number'
                    label='Amount (positive to add, negative to remove)'
                    placeholder='e.g. 50 or -25'
                    value={adjustAmount}
                    onChange={e => setAdjustAmount(e.target.value)}
                    className='flex-1'
                  />
                </div>
                <Input
                  label='Reason (required for audit trail)'
                  placeholder='e.g. Courtesy credit, Refund for issue #123'
                  value={adjustReason}
                  onChange={e => setAdjustReason(e.target.value)}
                />
                <div className='flex justify-end'>
                  <Button
                    variant='primary'
                    className='h-8 text-sm'
                    onClick={handleAdjust}
                    isDisabled={isAdjusting || !adjustAmount || !adjustReason.trim()}
                  >
                    {isAdjusting ? 'Adjusting...' : 'Apply Adjustment'}
                  </Button>
                </div>
              </div>
            </div>

            <div className='border rounded-lg p-4 bg-amber-50 border-amber-200'>
              <h4 className='text-sm font-semibold text-amber-800 mb-3'>
                Credit Overrides{' '}
                <span className='text-xs font-normal text-amber-600'>(direct value set)</span>
              </h4>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='text-xs font-medium text-gray-600 mb-1 block'>
                    Purchased Credits
                  </label>
                  <input
                    type='number'
                    className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                    value={overridePurchased}
                    onChange={e => setOverridePurchased(e.target.value)}
                    placeholder='0'
                    min='0'
                  />
                </div>
                <div>
                  <label className='text-xs font-medium text-gray-600 mb-1 block'>
                    Carried Over Credits
                  </label>
                  <input
                    type='number'
                    className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                    value={overrideCarriedOver}
                    onChange={e => setOverrideCarriedOver(e.target.value)}
                    placeholder='0'
                    min='0'
                  />
                </div>
                <div>
                  <label className='text-xs font-medium text-gray-600 mb-1 block'>
                    Credit Debt
                  </label>
                  <input
                    type='number'
                    className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                    value={overrideCreditDebt}
                    onChange={e => setOverrideCreditDebt(e.target.value)}
                    placeholder='0'
                    min='0'
                  />
                </div>
                <div>
                  <label className='text-xs font-medium text-gray-600 mb-1 block'>
                    Carryover Debt{' '}
                    <span className='text-xs font-normal text-amber-600'>(Flex overage)</span>
                  </label>
                  <input
                    type='number'
                    className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                    value={overrideCarryoverDebt}
                    onChange={e => setOverrideCarryoverDebt(e.target.value)}
                    placeholder='0'
                    min='0'
                  />
                </div>
              </div>
              <div className='flex justify-end mt-3'>
                <Button
                  variant='primary'
                  className='h-8 text-sm'
                  onClick={handleCreditOverride}
                  isDisabled={isOverriding}
                >
                  {isOverriding ? 'Saving...' : 'Save Overrides'}
                </Button>
              </div>
            </div>

            <div className='border rounded-lg p-4 bg-amber-50 border-amber-200'>
              <div className='flex items-center justify-between mb-2'>
                <h4 className='text-sm font-semibold text-amber-800'>Recover Lost Credits</h4>
                <Button
                  variant='outlined'
                  className='h-7 text-xs border-amber-400 text-amber-700'
                  onClick={handleCheckLostCredits}
                  isDisabled={isCheckingLost}
                >
                  {isCheckingLost ? 'Checking...' : 'Find Lost Credits'}
                </Button>
              </div>
              <p className='text-xs text-amber-700 mb-2'>
                Credits can get lost on expired licenses during plan upgrades. Click to scan all
                historical licenses for this account.
              </p>

              {lostCredits && lostCredits.length > 0 && (
                <div className='mt-3'>
                  <div className='bg-white rounded border border-amber-200 p-3 mb-3'>
                    {lostCredits.map((lc, idx) => (
                      <div key={idx} className='flex justify-between items-center py-1 text-sm'>
                        <span className='text-gray-600'>
                          License #{lc.license_id}
                          {lc.is_deleted ? ' (deleted)' : ' (expired)'}
                        </span>
                        <span className='font-semibold text-amber-700'>
                          {lc.purchased_credits} credits
                        </span>
                      </div>
                    ))}
                    <div className='border-t mt-2 pt-2 flex justify-between items-center'>
                      <span className='text-sm font-semibold text-gray-700'>
                        Total recoverable:{' '}
                        {lostCredits.reduce((sum, lc) => sum + lc.purchased_credits, 0)}
                      </span>
                      <Button
                        variant='primary'
                        className='h-7 text-xs bg-amber-600 hover:bg-amber-700'
                        onClick={handleRecoverCredits}
                        isDisabled={isRecovering}
                      >
                        {isRecovering ? 'Recovering...' : 'Recover All'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {lostCredits && lostCredits.length === 0 && (
                <div className='text-xs text-green-700 mt-1'>
                  No lost credits found — all credits are on the active license.
                </div>
              )}
            </div>

            {creditBalance.purchase_history.length > 0 && (
              <div>
                <h4 className='text-sm font-semibold text-gray-700 mb-2'>
                  Purchase History ({creditBalance.purchase_history.length})
                </h4>
                <div className='border rounded-lg overflow-hidden'>
                  <table className='w-full text-sm'>
                    <thead className='bg-gray-50 border-b'>
                      <tr>
                        <th className='text-left px-3 py-2 text-gray-500 font-medium'>Credits</th>
                        <th className='text-left px-3 py-2 text-gray-500 font-medium'>Type</th>
                        <th className='text-left px-3 py-2 text-gray-500 font-medium'>Date</th>
                        <th className='text-left px-3 py-2 text-gray-500 font-medium'>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditBalance.purchase_history
                        .sort((a, b) => (b.purchased_at ?? '').localeCompare(a.purchased_at ?? ''))
                        .map((purchase, idx) => (
                          <tr key={idx} className='border-b last:border-0 hover:bg-gray-50'>
                            <td className='px-3 py-2'>
                              <span
                                className={`font-semibold ${purchase.credits >= 0 ? 'text-green-600' : 'text-red-600'}`}
                              >
                                {purchase.credits >= 0 ? '+' : ''}
                                {purchase.credits}
                              </span>
                            </td>
                            <td className='px-3 py-2'>
                              <Chip
                                size='sm'
                                variant='flat'
                                color={
                                  purchase.pack_type === 'admin_adjustment'
                                    ? 'warning'
                                    : purchase.refunded
                                      ? 'danger'
                                      : 'success'
                                }
                              >
                                {purchase.pack_type === 'admin_adjustment'
                                  ? 'Admin'
                                  : purchase.refunded
                                    ? 'Refunded'
                                    : purchase.pack_type || 'Purchase'}
                              </Chip>
                            </td>
                            <td className='px-3 py-2 text-gray-500'>
                              {purchase.purchased_at ? formatDate(purchase.purchased_at) : '-'}
                            </td>
                            <td className='px-3 py-2 text-gray-500 text-xs'>
                              {purchase.admin_email && <span>by {purchase.admin_email}</span>}
                              {purchase.reason && (
                                <span className='italic'> — {purchase.reason}</span>
                              )}
                              {purchase.amount_total_cents != null && (
                                <span> (${(purchase.amount_total_cents / 100).toFixed(2)})</span>
                              )}
                              {purchase.receipt_url && (
                                <a
                                  href={purchase.receipt_url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='ml-1 text-blue-600 hover:underline'
                                >
                                  Receipt
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className='text-center text-gray-500 py-8'>No credit data available</div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant='outlined' onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}
